import { useEffect, useState } from "react";

const LOGO_URL =
  "https://vibe.filesafe.space/1778679421893490393/attachments/45d9bd93-e98b-4f65-bfcf-0d98c7cfc665.png";

/**
 * Branded modern page loader matching str.clickacquire.ai:
 * - Subtle radial ambient glow behind the loader
 * - Smooth conic-gradient masked spinner ring
 * - Outer subtle border ring
 * - Centered logo image inside the spinner
 * - Refined uppercase slogan
 * - 3 animated bouncing status dots
 */
const PageLoader = () => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const dismiss = () => setHidden(true);
    const t = setTimeout(dismiss, 1500);
    window.addEventListener("load", dismiss);
    return () => {
      clearTimeout(t);
      window.removeEventListener("load", dismiss);
    };
  }, []);

  return (
    <div
      aria-hidden={hidden}
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background transition-opacity duration-700 ${
        hidden ? "pointer-events-none opacity-0" : "opacity-100"
      }`}
    >
      {/* Soft ambient radial glow behind the loader (like str.clickacquire.ai) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 45%, hsl(var(--primary) / 0.14), transparent 70%)",
        }}
      />

      <div className="relative flex flex-col items-center">
        {/* Spinner container: 128px circle */}
        <div className="relative flex h-32 w-32 items-center justify-center">
          {/* Subtle outer border track */}
          <div className="absolute inset-0 rounded-full border border-border/60" />

          {/* Exact conic-gradient masked spinning ring from str.clickacquire.ai */}
          <div
            className="absolute inset-0 rounded-full animate-spin-slow"
            style={{
              background:
                "conic-gradient(from 0deg, hsl(var(--primary)) 0deg, hsl(var(--primary) / 0.3) 120deg, transparent 230deg, transparent 360deg)",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px))",
            }}
          />

          {/* Real logo image centered inside */}
          <div className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-background/90 p-2 shadow-xs">
            <img
              src={LOGO_URL}
              alt="JNN Homes"
              className="h-full w-full object-contain"
            />
          </div>
        </div>

        {/* Slogan */}
        <p className="mt-7 text-center font-body text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
          Airbnb Management · VA &amp; DC
        </p>

        {/* 3 pulsing bounce dots matching str.clickacquire.ai */}
        <div className="mt-4 flex items-center gap-1.5">
          <span
            className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce-dot"
            style={{ animationDelay: "-0.3s" }}
          />
          <span
            className="h-1.5 w-1.5 rounded-full bg-primary/70 animate-bounce-dot"
            style={{ animationDelay: "-0.15s" }}
          />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/40 animate-bounce-dot" />
        </div>
      </div>
    </div>
  );
};

export default PageLoader;
